import axios from 'axios'
import type { AxiosError, AxiosResponse } from 'axios'

// API configuration with validation
const isDev = import.meta.env.DEV

// Validate required environment variables in production builds
if (!isDev && !import.meta.env.VITE_API_BASE_URL) {
  throw new Error(
    'VITE_API_BASE_URL is required in production builds. ' +
    'Please ensure the environment variable is set correctly in your build process.'
  )
}

const API_BASE_URL = isDev ? '' : import.meta.env.VITE_API_BASE_URL

/**
 * Axios instance for authenticated API calls
 * Uses cookies for authentication
 */
export const apiClient = axios.create({
  baseURL: isDev ? '/api' : `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true,
  timeout: 30000
})

/**
 * Axios instance for public (unauthenticated) API calls
 * Used for the Unreg API
 * According to documentation, these endpoints are prefixed with /unreg-api
 */
export const publicClient = axios.create({
  baseURL: isDev ? '/unreg-api' : `${API_BASE_URL}/unreg-api`,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 30000
})

// Error handler that standardizes network error responses
const handleNetworkError = (error: AxiosError) => {
  // Only handle actual network errors, not API errors (code !== 0)
  // API errors will be handled by ResponseHandler
  const response = error.response?.data

  if (response && typeof response === 'object' && 'code' in response && 'message' in response) {
    // This is an API response with error code - pass it through as-is
    // ResponseHandler will handle the code checking
    return Promise.reject({
      code: response.code,
      message: response.message,
      originalError: error,
    })
  }

  // This is a real network error (no response, timeout, etc.)
  return Promise.reject({
    code: -1,
    message: error.message || 'Network error',
    originalError: error,
  })
}

// Success interceptor - just pass through, let ResponseHandler handle code checking
const passThrough = (response: AxiosResponse) => {
  // Don't check for API errors here - let ResponseHandler do it
  // This prevents double error handling
  return response
}

// Add response interceptors to both clients
apiClient.interceptors.response.use(passThrough, handleNetworkError)
publicClient.interceptors.response.use(passThrough, handleNetworkError)

/**
 * Axios instance for external cryptocurrency API calls (CoinGecko)
 * Separate instance for external services with different error handling
 */
export const cryptoClient = axios.create({
  baseURL: 'https://api.coingecko.com/api/v3',
  timeout: 10000,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
})

// Simple error logging for external API - main error handling done in queries layer
cryptoClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('[CoinGecko API]', error.message)
    // Re-throw error to be handled by queries layer
    return Promise.reject(error)
  }
)

/**
 * Checks if the current error represents an authentication error (code 2000)
 */
export const isAuthError = (error: any): boolean => {
  return error && error.code === 2000
} 